import type { Character } from '../domain/character.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import { inEncounter } from '../domain/runtime.ts';
import type { Skill } from '../content/types.ts';
import type { HotlistCatalogue } from '../rules/hotlist.ts';
import { encounterActionProblem } from '../rules/encounter.ts';
import type { EncounterActionTiming } from '../rules/encounter.ts';
import { isPassiveSpell, isSpell, spellCastActionCost } from '../rules/spellCast.ts';
import { spendEncounterAction } from './encounterService.ts';
import { spendCharacterMana } from './healthService.ts';
import { healPetHealthSlots } from './petHealthService.ts';
import { petMaxHealthSlots } from '../domain/pet.ts';

export type PetTargetSpellExecution = 'normal' | 'healing-interrupt';

export type PetTargetSpellOutcome =
  | { ok: true; character: Character; runtime: RuntimeState; lines: string[]; actionsSpent: number }
  | { ok: false; reason: string };

export function castPetTargetSupportSpell(
  character: Character,
  runtime: RuntimeState,
  petId: string,
  skill: Skill,
  deps: { catalogue: HotlistCatalogue },
  execution: PetTargetSpellExecution,
  now: string,
): PetTargetSpellOutcome {
  if (runtime.characterId !== character.id) return { ok: false, reason: 'The combat runtime belongs to a different crawler.' };
  if (!isSpell(skill) || !isPassiveSpell(skill)) return { ok: false, reason: `${skill.name} is not a passive Spell.` };
  const known = character.skills.find((entry) => entry.skillId === skill.id);
  if (!known || known.rank < 1) return { ok: false, reason: `${skill.name} is not a Spell this crawler knows.` };
  if (skill.maxRank !== undefined && known.rank > skill.maxRank) return { ok: false, reason: `${skill.name} exceeds its printed Rank limit.` };
  const effect = skill.supportEffect;
  if (!effect || effect.target !== 'pet') return { ok: false, reason: `${skill.name} does not have an automated pet-target effect.` };
  if (execution === 'healing-interrupt' && !skill.traits.includes('interrupt')) return { ok: false, reason: `${skill.name} is not an Interrupt.` };

  const pet = character.companions.find((entry) => entry.id === petId);
  if (!pet) return { ok: false, reason: 'That companion is no longer attached to this crawler.' };
  if (pet.healthBarSlotsRemaining <= 0) return { ok: false, reason: `${pet.name} is dead and cannot be healed by this effect.` };

  const actionCost = spellCastActionCost(character, skill.id, deps.catalogue);
  const timing: EncounterActionTiming = execution === 'healing-interrupt' ? 'interrupt' : 'normal';
  const timingProblem = encounterActionProblem(runtime, character.id, actionCost, timing);
  if (timingProblem !== null) return { ok: false, reason: `${skill.name} costs ${actionCost} Action${actionCost === 1 ? '' : 's'}. ${timingProblem}` };

  const manaCost = skill.manaCost ?? 0;
  const paid = spendCharacterMana(character, manaCost);
  if (!paid.ok) return { ok: false, reason: `${skill.name} costs ${manaCost} Mana. ${paid.reason}` };

  let nextCharacter = paid.character;
  const livePet = nextCharacter.companions.find((entry) => entry.id === petId)!;
  if (effect.kind === 'heal-full') {
    const max = petMaxHealthSlots(livePet);
    const healed = healPetHealthSlots(livePet, max, now);
    nextCharacter = {
      ...nextCharacter,
      companions: nextCharacter.companions.map((entry) => entry.id === petId ? healed : entry),
      updatedAt: now,
    };
  }

  let nextRuntime = runtime;
  let actionsSpent = 0;
  const lines = [`${skill.name} cast on ${livePet.name} for ${manaCost} Mana: Health Bar restored to 100%.`];
  if (inEncounter(runtime)) {
    const spent = spendEncounterAction(runtime, character.id, actionCost, timing);
    if (!spent.ok) return { ok: false, reason: spent.reason };
    nextRuntime = spent.runtime;
    actionsSpent = actionCost;
    lines.push(`${actionCost} Action${actionCost === 1 ? '' : 's'} spent${actionCost === 2 ? ' - Spell was not prepared in the Hotlist' : ''}.`);
  }
  return { ok: true, character: nextCharacter, runtime: nextRuntime, lines, actionsSpent };
}
